import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  ScrollView,
  Platform,
  ActivityIndicator
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores } from '../../styles/variablesCss';
import { formatPrecio } from '../../utils/formatPrice';
import { useNavigation } from '@react-navigation/native';
import { useCart } from '../../context/CartContext';
const ResumenCompra = () => {
  const [expanded] = useState(true);
  const [deletingPlu, setDeletingPlu] = useState(null);
  const [updatingPlu, setUpdatingPlu] = useState(null);
  const redirectedRef = useRef(false);
  const navigation = useNavigation?.();
  
  // Obtener productos, función y estado de carga del contexto del carrito
  const { productos, agregarAlCarrito, loading, cargarProductosCarrito } = useCart();
  
  // Estado para controlar responsividad
  const [windowWidth, setWindowWidth] = useState(
    Platform.OS === 'web' && typeof window !== 'undefined' ? window.innerWidth : 768
  );
  const isMobile = windowWidth < 768;

  // Cargar productos del carrito al montar el componente
  useEffect(() => {
    cargarProductosCarrito();
  }, []);

  // Redirigir automáticamente a /home si no hay productos (después de cargar)
  useEffect(() => {
    if (redirectedRef.current) return;
    if (!loading && (!productos || productos.length === 0)) {
      redirectedRef.current = true;
      if (Platform.OS === 'web') {
        try { window.location.href = '/'; } catch (e) {}
      } else if (navigation && navigation.navigate) {
        navigation.navigate('');
      }
    }
  }, [loading, productos, navigation]);

  // Listener para cambios de tamaño de ventana
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);
  
  // Función para eliminar un producto del carrito
  const eliminarProducto = async (plu, cantidadEnCarrito) => {
    try {
      setDeletingPlu(plu);
      // Enviar cantidad negativa para eliminar todo el producto
      await agregarAlCarrito(plu, -cantidadEnCarrito);
    } catch (error) {
      console.error('Error al eliminar producto:', error);
    } finally {
      setDeletingPlu(null);
    }
  };

  // Función para incrementar cantidad
  const incrementarCantidad = async (plu, minStock) => {
    try {
      setUpdatingPlu(plu);
      // Usar minStock como incremento (igual que PlaceHolderProducto)
      const incremento = minStock ? parseFloat(minStock) : 1;
      await agregarAlCarrito(plu, incremento);
    } catch (error) {
      console.error('Error al incrementar cantidad:', error);
    } finally {
      setUpdatingPlu(null);
    }
  };

  // Función para decrementar cantidad
  const decrementarCantidad = async (plu, cantidadActual, minStock) => {
    try {
      setUpdatingPlu(plu);
      // Usar minStock como decremento (igual que PlaceHolderProducto)
      const decremento = minStock ? parseFloat(minStock) : 1;
      if (cantidadActual > decremento) {
        // Si hay más que el mínimo, restar el decremento
        await agregarAlCarrito(plu, -decremento);
      } else {
        // Si es igual o menor al mínimo, eliminar el producto completo
        await eliminarProducto(plu, cantidadActual);
      }
    } catch (error) {
      console.error('Error al decrementar cantidad:', error);
    } finally {
      setUpdatingPlu(null);
    }
  };
  // Estilos dinámicos
  const dynamicStyles = {
    tablaResumen: {
      ...styles.tablaResumen,
      marginBottom: isMobile ? 10 : 20,
    },
    tablaBody: {
      ...styles.tablaBody,
      maxHeight: isMobile ? 400 : 300,
    },
    productoImagen: {
      ...styles.productoImagen,
      width: isMobile ? 60 : 60,
      height: isMobile ? 60 : 60,
      marginRight: isMobile ? 12 : 10,
    },
    productoNombre: {
      ...styles.productoNombre,
      fontSize: isMobile ? 13 : 14,
      marginBottom: isMobile ? 8 : 0,
    },
    tablaHeader: {
      ...styles.tablaHeader,
      display: isMobile ? 'none' : 'flex',
    },
  };

  // Mostrar loading mientras se cargan los productos
  if (loading && (!productos || productos.length === 0)) {
    return (
      <View style={styles.columnaResumen}>
        <View style={styles.emptyContainer}>
          <ActivityIndicator size="large" color={colores.rojo} />
          <Text style={styles.loadingText}>Cargando carrito...</Text>
        </View>
      </View>
    );
  }

  // Si no hay productos después de cargar, mostrar mensaje
  if (!productos || productos.length === 0) {
    return (
      <View style={styles.columnaResumen}>
        <View style={styles.emptyContainer}>
          <MaterialCommunityIcons name="cart-outline" size={50} color={colores.grisClaro} />
          <Text style={styles.emptyText}>No hay productos en el carrito</Text>
        </View>
      </View>
    );
  }

  return (
    <>
      <View style={styles.columnaResumen}>
        {/* Header desplegable */}
        {expanded && (
        <View style={dynamicStyles.tablaResumen}>
          <View style={dynamicStyles.tablaHeader}>
            <Text style={[styles.tablaColumna, styles.productoColumna]}>PRODUCTO</Text>
            <Text style={[styles.tablaColumna, styles.cantidadColumna]}>CANTIDAD</Text>
            <Text style={[styles.tablaColumna, styles.precioColumna]}>PRECIO</Text>
            <Text style={[styles.tablaColumna, styles.totalColumna]}>TOTAL</Text>
            <Text style={[styles.tablaColumna, styles.accionesColumna]}></Text>
          </View>

          <ScrollView style={dynamicStyles.tablaBody}>
            {productos.map((producto) => {
              const unitPrice = Number(producto?.precio) || 0;
              const quantity = Number(producto?.cantCarrito) || 0;
              const totalPrice = unitPrice * quantity;

              return isMobile ? (
                // Vista mejorada tipo card para móvil
                <View key={producto.plu} style={styles.mobileCard}>
                  <View style={styles.mobileCardContent}>
                    <Image 
                      source={{uri: producto.foto}} 
                      style={dynamicStyles.productoImagen} 
                      resizeMode="contain"
                    />
                    <View style={styles.mobileCardInfo}>
                      <Text style={dynamicStyles.productoNombre} numberOfLines={2}>
                        {producto.descripcion || producto.nombre}
                      </Text>
                      
                      <View style={styles.mobileCardDetails}>
                        <View style={styles.detailRow}>
                          <Text style={styles.detailLabel}>Cantidad:</Text>
                          <View style={styles.quantityControls}>
                            <TouchableOpacity 
                              style={styles.quantityButton}
                              onPress={() => decrementarCantidad(producto.plu, producto.cantCarrito, producto.minStock)}
                              disabled={updatingPlu === producto.plu}
                            >
                              <MaterialCommunityIcons name="minus" size={16} color={colores.rojo} />
                            </TouchableOpacity>
                            <Text style={styles.quantityText}>{producto.cantCarrito}</Text>
                            <TouchableOpacity 
                              style={styles.quantityButton}
                              onPress={() => incrementarCantidad(producto.plu, producto.minStock)}
                              disabled={updatingPlu === producto.plu}
                            >
                              <MaterialCommunityIcons name="plus" size={16} color={colores.rojo} />
                            </TouchableOpacity>
                          </View>
                        </View>
                        
                        <View style={styles.detailRow}>
                          <Text style={styles.detailLabel}>Precio:</Text>
                          <Text style={styles.detailValue}>$ {formatPrecio(unitPrice, true)}</Text>
                        </View>
                        
                        <View style={styles.detailRow}>
                          <Text style={styles.detailLabelBold}>Total:</Text>
                          <Text style={styles.detailValueBold}>
                            $ {formatPrecio(totalPrice, true)}
                          </Text>
                        </View>
                      </View>
                    </View>
                  </View>
                  
                  <TouchableOpacity 
                    style={styles.mobileDeleteButton}
                    onPress={() => eliminarProducto(producto.plu, producto.cantCarrito)}
                    disabled={deletingPlu === producto.plu}
                  >
                    {deletingPlu === producto.plu ? (
                      <ActivityIndicator size="small" color={colores.rojo} />
                    ) : (
                      <MaterialCommunityIcons name="delete" size={22} color={colores.rojo} />
                    )}
                  </TouchableOpacity>
                </View>
              ) : (
                // Vista de tabla para desktop
                <View key={producto.plu} style={styles.tablaFila}>
                  <View style={[styles.tablaColumna, styles.productoColumna]}>
                    <View style={styles.productoContenido}>
                      <Image 
                        source={{uri: producto.foto}} 
                        style={dynamicStyles.productoImagen} 
                        resizeMode="contain"
                      />
                      <Text style={dynamicStyles.productoNombre}>{producto.descripcion || producto.nombre}</Text>
                    </View>
                  </View>
                  
                  <View style={[styles.tablaColumna, styles.cantidadColumna]}>
                    <View style={styles.quantityControlsDesktop}>
                      <TouchableOpacity 
                        style={styles.quantityButtonDesktop}
                        onPress={() => decrementarCantidad(producto.plu, producto.cantCarrito, producto.minStock)}
                        disabled={updatingPlu === producto.plu}
                      >
                        <MaterialCommunityIcons name="minus" size={18} color={colores.rojo} />
                      </TouchableOpacity>
                      <Text style={styles.quantityTextDesktop}>{producto.cantCarrito}</Text>
                      <TouchableOpacity 
                        style={styles.quantityButtonDesktop}
                        onPress={() => incrementarCantidad(producto.plu, producto.minStock)}
                        disabled={updatingPlu === producto.plu}
                      >
                        <MaterialCommunityIcons name="plus" size={18} color={colores.rojo} />
                      </TouchableOpacity>
                    </View>
                  </View>
                  <Text style={[styles.tablaColumna, styles.precioColumna]}>
                    $ {formatPrecio(unitPrice, true)}
                  </Text>
                  <Text style={[styles.tablaColumna, styles.totalColumna]}>
                    $ {formatPrecio(totalPrice, true)}
                  </Text>
                  
                  <View style={[styles.tablaColumna, styles.accionesColumna]}>
                    <TouchableOpacity
                      onPress={() => eliminarProducto(producto.plu, producto.cantCarrito)}
                      disabled={deletingPlu === producto.plu}
                    >
                      {deletingPlu === producto.plu ? (
                        <ActivityIndicator size="small" color={colores.rojo} />
                      ) : (
                        <MaterialCommunityIcons name="delete" size={20} color={colores.rojo} />
                      )}
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })}
          </ScrollView>
        </View>
        )}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  columnaResumen: {
    backgroundColor: colores.blanco,
    marginBottom: Platform.OS === 'web' ? 0 : 20,
    alignItems: 'center',
    borderRadius: 5,
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.1,
          shadowRadius: 2,
          elevation: 2,
        }
    ),
  },
  tablaResumen: {
    marginBottom: 20,
    width: '100%',
  },
  tablaHeader: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: colores.rojo,
    paddingVertical: 10,
    marginBottom: 5,
  },
  tablaBody: {
    maxHeight: 300,
  },
  tablaFila: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colores.grisClaro,
    alignItems: 'center',
  },
  tablaColumna: {
    padding: 10,
    justifyContent: 'center',
  },
  productoColumna: {
    flex: 4,
  },
  cantidadColumna: {
    flex: 1,
    textAlign: 'center',
  },
  precioColumna: {
    flex: 1,
    textAlign: 'right',
  },
  totalColumna: {
    flex: 1,
    textAlign: 'right',
  },
  accionesColumna: {
    flex: 0.5,
    alignItems: 'center',
  },
  productoContenido: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  productoImagen: {
    width: 60,
    height: 60,
    marginRight: 10,
    borderRadius: 4,
    backgroundColor: '#f5f5f5',
  },
  productoNombre: {
    fontSize: 14,
    fontWeight: '500',
    color: colores.texto,
    flex: 1,
  },
  // Estilos para vista móvil tipo card
  mobileCard: {
    backgroundColor: colores.blanco,
    marginBottom: 10,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    position: 'relative',
  },
  mobileCardContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingRight: 35,
  },
  mobileCardInfo: {
    flex: 1,
  },
  mobileCardDetails: {
    marginTop: 8,
    gap: 6,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  detailLabel: {
    fontSize: 12,
    color: '#666',
    fontWeight: '400',
  },
  detailValue: {
    fontSize: 13,
    color: colores.texto,
    fontWeight: '500',
  },
  detailLabelBold: {
    fontSize: 13,
    color: '#666',
    fontWeight: '600',
  },
  detailValueBold: {
    fontSize: 14,
    color: colores.rojo,
    fontWeight: 'bold',
  },
  mobileDeleteButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    padding: 4,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
    minHeight: 200,
  },
  emptyText: {
    fontSize: 16,
    color: colores.grisClaro,
    marginTop: 10,
  },
  loadingText: {
    fontSize: 16,
    color: colores.texto,
    marginTop: 15,
    fontWeight: '500',
  },
  // Estilos para controles de cantidad (móvil)
  quantityControls: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  quantityButton: {
    padding: 4,
    borderRadius: 4,
    backgroundColor: colores.blanco,
    borderWidth: 1,
    borderColor: colores.rojo,
  },
  quantityText: {
    fontSize: 14,
    fontWeight: '600',
    color: colores.texto,
    marginHorizontal: 12,
    minWidth: 25,
    textAlign: 'center',
  },
  // Estilos para controles de cantidad (desktop)
  quantityControlsDesktop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 4,
  },
  quantityButtonDesktop: {
    padding: 6,
    borderRadius: 4,
    backgroundColor: colores.blanco,
    borderWidth: 1,
    borderColor: colores.rojo,
  },
  quantityTextDesktop: {
    fontSize: 15,
    fontWeight: '600',
    color: colores.texto,
    marginHorizontal: 15,
    minWidth: 30,
    textAlign: 'center',
  },
});

export default ResumenCompra;
